import React, { useEffect, useRef, useState } from "react";

export type ViewerDisplayMode = "ONLY_2D" | "ONLY_3D" | "BOTH_2D_3D";

const iconStyle: React.CSSProperties = {
  display: "block",
  width: 15,
  height: 15,
  flex: "0 0 auto",
};

const MapIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path
      d="M4 6.5 9 4l6 2.5L20 4v13.5l-5 2.5-6-2.5L4 20V6.5Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <path d="M9 4v13.5M15 6.5V20" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const CubeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path
      d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z"
      fill="none"
      stroke="currentColor"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <path
      d="M4.4 7.7 12 12l7.6-4.3M12 12v8.5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const SplitViewIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path d="M4 5h6v14H4V5ZM14 5h6v14h-6V5Z" fill="currentColor" />
  </svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path
      d="M7 10V8a5 5 0 0 1 10 0v2M6 10h12v10H6V10Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const UnlockIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path
      d="M7 10V8a5 5 0 0 1 9.6-1.9M6 10h12v10H6V10Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path
      d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
    <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path
      d="m3 3 18 18M10.6 10.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-4.8M7.3 7.5C4.3 9.2 2.5 12 2.5 12s3.5 6 9.5 6c1.7 0 3.2-.5 4.5-1.2M19.1 14.1c1.5-1.1 2.4-2.1 2.4-2.1s-3.5-6-9.5-6c-.8 0-1.6.1-2.3.3"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const ScissorIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <circle cx="6" cy="6" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="6" cy="18" r="3" fill="none" stroke="currentColor" strokeWidth="2" />
    <path
      d="M20 4 8.12 15.88M14.47 14.48 20 20M8.12 8.12 12 12"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const GridIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path
      d="M3 3h18v18H3V3Zm6 0v18M15 3v18M3 9h18M3 15h18"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const SyncArrowsIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" style={iconStyle}>
    <path
      d="M4 9h13l-4-4M20 15H7l4 4"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
);

const MODE_OPTIONS: Array<{ value: ViewerDisplayMode; label: React.ReactNode }> = [
  {
    value: "ONLY_2D",
    label: (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <MapIcon />
        2D
      </span>
    ),
  },
  {
    value: "ONLY_3D",
    label: (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <CubeIcon />
        3D
      </span>
    ),
  },
  {
    value: "BOTH_2D_3D",
    label: (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <SplitViewIcon />
        2D + 3D
      </span>
    ),
  },
];

const modeButtonBaseStyle: React.CSSProperties = {
  height: 30,
  border: 0,
  borderRadius: 6,
  padding: "0 10px",
  background: "transparent",
  color: "#495057",
  cursor: "pointer",
  font: "inherit",
  fontSize: 12,
  lineHeight: 1,
};

interface ViewerModeButtonBarProps {
  displayMode: ViewerDisplayMode;
  onDisplayModeChange: (displayMode: ViewerDisplayMode) => void;
  syncViewports: boolean;
  onSyncViewportsChange: (syncViewports: boolean) => void;
  showIfcCoveringToggle?: boolean;
  hideIfcCovering?: boolean;
  onHideIfcCoveringChange?: (hideIfcCovering: boolean) => void;
  showSyncViewTo3D?: boolean;
  onSyncViewTo3D?: () => void;
  showClipperToggle?: boolean;
  clipperActive?: boolean;
  onClipperChange?: (active: boolean) => void;
  onDeleteAllClipPlanes?: () => void;
  showGridToggle?: boolean;
  gridVisible?: boolean;
  onGridVisibleChange?: (visible: boolean) => void;
}

export const ViewerModeButtonBar: React.FC<ViewerModeButtonBarProps> = ({
  displayMode,
  onDisplayModeChange,
  showIfcCoveringToggle = false,
  hideIfcCovering = true,
  onHideIfcCoveringChange,
  onSyncViewTo3D,
  showClipperToggle = false,
  clipperActive = false,
  onClipperChange,
  onDeleteAllClipPlanes,
  showGridToggle = false,
  gridVisible = false,
  onGridVisibleChange,
}) => {
  const [clipMenuOpen, setClipMenuOpen] = useState(false);
  const clipMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!clipMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (clipMenuRef.current && !clipMenuRef.current.contains(e.target as Node)) {
        setClipMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [clipMenuOpen]);

  return (
    <>
    <div
      style={{
        position: "absolute",
        top: 12,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 30,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <div
        style={{
          boxShadow: "0 2px 10px rgba(15, 23, 42, 0.16)",
          borderRadius: 8,
          background: "white",
          display: "flex",
          alignItems: "center",
          gap: 2,
          padding: 3,
        }}
      >
        {MODE_OPTIONS.map((option) => {
          const active = option.value === displayMode;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              onClick={() => onDisplayModeChange(option.value)}
              style={{
                ...modeButtonBaseStyle,
                background: active ? "#f1f3f5" : "transparent",
                color: active ? "#111827" : "#495057",
                boxShadow: active ? "0 1px 3px rgba(15, 23, 42, 0.14)" : undefined,
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {displayMode === "BOTH_2D_3D" && (
        <button
          type="button"
          title="Synka 2D vy till 3D"
          aria-label="Synka 2D vy till 3D"
          style={{
            width: 36,
            height: 36,
            border: "1px solid #dee2e6",
            borderRadius: 6,
            background: "white",
            color: "#495057",
            boxShadow: "0 2px 10px rgba(15, 23, 42, 0.16)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
          onClick={() => onSyncViewTo3D?.()}
        >
          <SyncArrowsIcon />
        </button>
      )}

      {showIfcCoveringToggle && (
        <button
          type="button"
          title={hideIfcCovering ? "Vis himling (IfcCovering)" : "Skjul himling (IfcCovering)"}
          aria-label={hideIfcCovering ? "Vis himling" : "Skjul himling"}
          aria-pressed={hideIfcCovering}
          style={{
            height: 36,
            border: hideIfcCovering ? "1px solid #228be6" : "1px solid #dee2e6",
            borderRadius: 6,
            background: hideIfcCovering ? "#228be6" : "white",
            color: hideIfcCovering ? "white" : "#495057",
            boxShadow: "0 2px 10px rgba(15, 23, 42, 0.16)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            padding: "0 10px",
            cursor: "pointer",
            font: "inherit",
            fontSize: 12,
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
          onClick={() => onHideIfcCoveringChange?.(!hideIfcCovering)}
        >
          {hideIfcCovering ? <EyeOffIcon /> : <EyeIcon />}
          Undertak
        </button>
      )}

    </div>

    {/* Vertical toolbar on the right for Klipp & Grid */}
    {(showClipperToggle || showGridToggle) && (
      <div
        style={{
          position: "absolute",
          top: 325,
          right: 42,
          zIndex: 30,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          background: "white",
          borderRadius: 8,
          boxShadow: "0 2px 10px rgba(15, 23, 42, 0.16)",
          padding: 3,
        }}
      >
        {showClipperToggle && (
          <div ref={clipMenuRef} style={{ position: "relative" }}>
            <button
              type="button"
              title={clipperActive ? "Slå av klipping" : "Aktiver klipping"}
              aria-label={clipperActive ? "Slå av klipping" : "Aktiver klipping"}
              aria-pressed={clipperActive}
              style={{
                width: 32,
                height: 32,
                border: 0,
                borderRadius: 6,
                padding: 0,
                background: clipperActive ? "#228be6" : "transparent",
                color: clipperActive ? "white" : "#495057",
                boxShadow: clipperActive ? "0 1px 3px rgba(15, 23, 42, 0.14)" : undefined,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
              onClick={() => onClipperChange?.(!clipperActive)}
              onContextMenu={(e) => {
                e.preventDefault();
                setClipMenuOpen((prev) => !prev);
              }}
            >
              <ScissorIcon />
            </button>
            {clipMenuOpen && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  right: "100%",
                  marginRight: 4,
                  background: "white",
                  borderRadius: 6,
                  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.18)",
                  padding: 4,
                  zIndex: 50,
                  minWidth: 120,
                }}
              >
                <button
                  type="button"
                  style={{
                    width: "100%",
                    border: 0,
                    borderRadius: 4,
                    padding: "6px 10px",
                    background: "transparent",
                    color: "#495057",
                    cursor: "pointer",
                    font: "inherit",
                    fontSize: 12,
                    textAlign: "left",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#f1f3f5"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
                  onClick={() => {
                    onDeleteAllClipPlanes?.();
                    setClipMenuOpen(false);
                  }}
                >
                  Fjern klipp
                </button>
              </div>
            )}
          </div>
        )}
        {showGridToggle && (
          <button
            type="button"
            title={gridVisible ? "Skjul rutenett" : "Vis rutenett"}
            aria-label={gridVisible ? "Skjul rutenett" : "Vis rutenett"}
            aria-pressed={gridVisible}
            style={{
              width: 32,
              height: 32,
              border: 0,
              borderRadius: 6,
              padding: 0,
              background: gridVisible ? "#228be6" : "transparent",
              color: gridVisible ? "white" : "#495057",
              boxShadow: gridVisible ? "0 1px 3px rgba(15, 23, 42, 0.14)" : undefined,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            onClick={() => onGridVisibleChange?.(!gridVisible)}
          >
            <GridIcon />
          </button>
        )}
      </div>
    )}
    </>
  );
};
